import { useState, useEffect, useMemo, useCallback } from 'react';
import { logWarn } from '../../monitoring/log';

const LOCAL_STORAGE_ITEM_KEY_PREFIX = 'm43-rate-limit-monitor';
const MILLIS_IN_24_HOURS = 1000 * 60 * 60 * 24;

type MonitorData = {
    count: number;
    hasNotified: boolean;
    lastResetTime: number;
};

export const useRateLimitMonitor = (
    monitorKey: string,
    limitCount: number,
    periodMs: number = MILLIS_IN_24_HOURS
) => {
    const localStorageItemKey = useMemo(() => `${LOCAL_STORAGE_ITEM_KEY_PREFIX}::${monitorKey}`, [
        monitorKey,
    ]);
    const [enabled, setEnabled] = useState<boolean>(true);

    const retrieveMonitorData = useCallback(() => {
        try {
            const item = localStorage.getItem(localStorageItemKey);
            if (item) {
                return JSON.parse(item);
            }
        } catch {
            setEnabled(false);
        }
        return { lastNotificationTime: 0, lastResetTime: Date.now(), count: 0 };
    }, [localStorageItemKey]);

    const storeMonitorData = useCallback(
        (data: MonitorData) => {
            try {
                localStorage.setItem(localStorageItemKey, JSON.stringify(data));
            } catch {
                setEnabled(false);
            }
        },
        [localStorageItemKey]
    );

    const [notificationData, setNotificationData] = useState<Record<string, unknown>>();
    const [monitorData, setMonitorData] = useState<MonitorData>(retrieveMonitorData);

    useEffect(
        function initialResetMonitorCheck() {
            const { lastResetTime } = retrieveMonitorData();
            const millisSinceReset = Date.now() - lastResetTime;

            if (millisSinceReset > periodMs) {
                const resetData = { count: 0, hasNotified: false, lastResetTime: Date.now() };
                storeMonitorData(resetData);
                setMonitorData(resetData);
            }
        },
        [periodMs, retrieveMonitorData, storeMonitorData]
    );

    useEffect(
        function processCountIncrement() {
            if (!enabled) {
                return;
            }

            const { hasNotified, lastResetTime, count } = retrieveMonitorData();
            const millisSinceReset = Date.now() - lastResetTime;

            if (millisSinceReset > periodMs) {
                // reset monitor
                const resetData = { count: 1, hasNotified: false, lastResetTime: Date.now() };
                storeMonitorData(resetData);
                setMonitorData(resetData);
            } else if (count >= limitCount && !hasNotified) {
                // emit notification
                logWarn(`Rate limit hit for monitor ${monitorKey}`, {
                    ...notificationData,
                });
                const updatedData = { ...monitorData, hasNotified: true };
                storeMonitorData(updatedData);
                setMonitorData(updatedData);
            }
        },
        [
            enabled,
            limitCount,
            monitorData,
            monitorKey,
            notificationData,
            periodMs,
            retrieveMonitorData,
            storeMonitorData,
        ]
    );

    const incrementCount = (notificationData: Record<string, unknown> = {}) => {
        if (!enabled) {
            return;
        }

        const updatedData = { ...monitorData, count: monitorData.count + 1 };
        storeMonitorData(updatedData);
        setMonitorData(updatedData);
        setNotificationData(notificationData);
    };

    return { incrementCount };
};
